import React from 'react';
import {
  ILandingPageBuilderForm,
  ILandingPageDesignBase,
} from '../../../types';
import { IoMdMail, IoMdPerson, IoMdPhonePortrait } from 'react-icons/io';
import { PiGlobe } from 'react-icons/pi';
import { Flex } from 'lib/components/styles/layout';
import { ICombinedUserData, useAuth } from 'lib/context';
import {
  HeaderContainer,
  Label,
  LabelContainer,
  LogoImage,
  LogoPlaceholder,
} from '../styles';
import { useTheme } from 'styled-components/macro';
import { HiOfficeBuilding } from 'react-icons/hi';
import { MdBadge } from 'react-icons/md';

interface IHeaderProps {
  values: ILandingPageBuilderForm;
  isMobile: boolean;
  screenshot?: boolean;
}

export const getHeaderLabels = (
  userData: ICombinedUserData,
  values: ILandingPageDesignBase,
  neutralColor: string,
  iconSize: number = 16
) => {
  const { firstName, lastName, phone1, phone2, website, dept, title, email } =
    userData;
  const {
    showFirstName,
    showLastName,
    showPhone1,
    showPhone2,
    showWebsite,
    showDepartment,
    showTitle,
    showEmail,
    headerTextColor,
  } = values;

  const name = `${showFirstName ? firstName : ''} ${showLastName ? lastName : ''}`;
  const color = headerTextColor || neutralColor;

  return [
    {
      text: phone1,
      icon: <IoMdPhonePortrait color={color} size={iconSize} />,
      show: phone1 && showPhone1,
    },
    {
      text: email,
      icon: <IoMdMail color={color} size={iconSize} />,
      show: email && showEmail,
    },
    {
      text: name,
      icon: <IoMdPerson color={color} size={iconSize} />,
      show: showFirstName || showLastName,
    },
    {
      text: phone2,
      icon: <IoMdPhonePortrait color={color} size={iconSize} />,
      show: phone2 && showPhone2,
    },
    {
      text: website,
      icon: <PiGlobe color={color} size={iconSize} />,
      show: website && showWebsite,
    },
    {
      text: dept,
      icon: <HiOfficeBuilding color={color} size={iconSize} />,
      show: dept && showDepartment,
    },
    {
      text: title,
      icon: <MdBadge color={color} size={iconSize} />,
      show: title && showTitle,
    },
  ];
};

export const Header = ({
  values,
  isMobile,
  screenshot = false,
}: IHeaderProps) => {
  const theme = useTheme();
  const { userData } = useAuth();

  const {
    showContactIcons,
    headerBackgroundColor,
    headerContentOrientation,
    headerContentAlignment,
    headerFontCase,
    headerFontFamily,
    headerFontWeight,
    headerLineHeight,
    titleFontSize,
    showHeaderShadow,
    headerLogoMaxWidth,
    headerLogoMaxHeight,
    headerTextColor,
  } = values;

  const logoUrl = values?.headerLogo.url;
  const color = headerTextColor || theme.colors.neutral[100];

  const headerLabels = getHeaderLabels(userData, values, color);

  return (
    <HeaderContainer
      headerBackgroundColor={headerBackgroundColor}
      showHeaderShadow={showHeaderShadow}
      isMobile={isMobile}
      screenshot={screenshot}
    >
      {!screenshot &&
        (logoUrl ? (
          <LogoImage
            src={logoUrl}
            alt='lp-preview-header-logo'
            isMobile={isMobile}
            headerLogoMaxWidth={headerLogoMaxWidth}
            headerLogoMaxHeight={headerLogoMaxHeight}
          />
        ) : (
          <LogoPlaceholder isMobile={isMobile}>Logo</LogoPlaceholder>
        ))}
      <LabelContainer
        headerContentOrientation={headerContentOrientation}
        headerContentAlignment={headerContentAlignment}
        isMobile={isMobile}
      >
        {headerLabels.map(
          (label, idx) =>
            Boolean(label.show) && (
              <Flex
                key={idx}
                flexDirection='row'
                gap='8px'
                alignItems='center'
                width='auto'
              >
                {!!showContactIcons && label.icon}
                <Label
                  color={color}
                  headerFontCase={headerFontCase}
                  headerFontWeight={headerFontWeight}
                  headerFontFamily={headerFontFamily}
                  headerFontSize={titleFontSize}
                  headerLineHeight={headerLineHeight}
                >
                  {label.text}
                </Label>
              </Flex>
            )
        )}
      </LabelContainer>
    </HeaderContainer>
  );
};
